import clsx from 'clsx'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ComponentPropsWithRef,
  type ReactElement,
} from 'react'

import { HOMEPAGE_HERO_IMAGE_COLUMNS } from '@constants/globals'

import { useHeroImageSeed } from '@contexts/hero-image-context'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { getPictureAttributes } from '@components/contentful-content-types/asset-image/utils'
import { Column } from '@components/ui-core/column/column'
import { Figure } from '@components/ui-core/figure/figure'
import { Picture } from '@components/ui-core/picture/picture'
import { Row } from '@components/ui-core/row/row'
import { Section } from '@components/ui-core/section/section'
import { Stack } from '@components/ui-core/stack/stack'
import { mergeRefs } from '@components/ui-core/utils/merge-refs'

import { Fireflies } from '@custom/components/fireflies/fireflies'
import { getRandomHeroPerson, homepageHeroPersonImages } from '@custom/shared/const'

import { HeroButton } from '../hero-button/hero-button'
import { HomepageText } from '../text/homepage-text'
import { type HomepageData } from '../types'

import { Agents } from './agents'
import { Chat } from './chat'
import { Editor } from './editor'
import { Mail } from './mail'
import { Tool } from './tool'

import styles from './hero.module.css'

interface HeroProps extends ComponentPropsWithRef<'section'> {
  data?: HomepageData['heroSection']
}

export function Hero(props: HeroProps): ReactElement {
  const { className, children, data, ref, ...rest } = props
  const { heading, link, title } = data?.content || {}
  const heroRef = useRef<HTMLElement | null>(null)
  const [hasFireflies, setHasFireflies] = useState(false)

  // Get heroImageSeed from context - this ensures consistency with server-side preloading
  const heroImageSeed = useHeroImageSeed()

  // Use deterministic selection with heroImageSeed to match server-side preloading
  const personImage = heroImageSeed
    ? getRandomHeroPerson(heroImageSeed)
    : homepageHeroPersonImages[0]
  const personPictureAttributes = getPictureAttributes({
    width: personImage.width ?? 0,
    height: personImage.height ?? 0,
    url: updateAssetCDNUrl(personImage?.path),
    isSvg: false,
    cols: HOMEPAGE_HERO_IMAGE_COLUMNS,
  })

  useEffect(() => {
    // The Fireflies animation works only on hover,
    // checking if hover is available can help us avoid unnecessary rendering
    if (window.matchMedia('(hover: hover)').matches) {
      // Hover is available, so we can show the fireflies
      setHasFireflies(true)
    } else {
      // No hover is available, so we don't need to show the fireflies
      setHasFireflies(false)
    }
  }, [])

  return (
    <Section
      ref={mergeRefs(ref, heroRef)}
      className={clsx(styles.hero, className)}
      data-color-scheme="dark"
      {...rest}
    >
      <Column crossAxisAlignment="center" className={styles.titleGroup}>
        <HomepageText as="h1" variant="heroTitle" className={styles.title}>
          {title}
        </HomepageText>
        <HomepageText as="h2" variant="heroHeading" className={styles.heading}>
          {heading}
        </HomepageText>
        <HeroButton text={link?.fields?.title ?? 'Get Superhuman'} href={link?.fields?.targetUrl} />
      </Column>
      <Stack className={styles.visualsContainer}>
        <Figure className={clsx('full-bleed', styles.personPictureContainer)}>
          <Picture
            src={personPictureAttributes.src}
            srcSets={personPictureAttributes.srcSets}
            sizes={personPictureAttributes.sizes}
            width={personPictureAttributes.width}
            height={personPictureAttributes.height}
            alt="Person thinking"
            className={styles.personPicture}
            fetchPriority="high"
            loading="eager"
            imgClassName={styles.personImage}
          />
        </Figure>
        <Column gap="6x" className={styles.visualGroup1}>
          <Row gap="6x" crossAxisAlignment="center" className={styles.chat}>
            <Chat
              className={clsx(styles.visual, styles.visual1)}
              style={{ '--index': 1 } as CSSProperties}
            />
            <Agents
              className={clsx(styles.visual, styles.visual2)}
              style={{ '--index': 2 } as CSSProperties}
            />
          </Row>
          <Stack className={styles.mail}>
            <Mail
              className={clsx(styles.visual, styles.visual6)}
              style={{ '--index': 6 } as CSSProperties}
            />
            <Tool
              variant="scheduler"
              text="Schedule 15 minutes for a quick meeting with Mike"
              className={clsx(styles.visual, styles.visual7)}
              style={{ '--index': 7 } as CSSProperties}
            />
          </Stack>
        </Column>
        <Column gap="6x" crossAxisAlignment="end" className={styles.visualGroup2}>
          <Tool
            variant="proofreader"
            text="Tailor this language for executives"
            className={styles.visual}
            style={{ '--index': 3 } as CSSProperties}
          />
          <Editor
            className={clsx(styles.visual, styles.visual4)}
            style={{ '--index': 4 } as CSSProperties}
          />
          <Tool
            variant="grammarly"
            text="Proofread with Grammarly"
            className={clsx(styles.visual, styles.visual5)}
            style={{ '--index': 5 } as CSSProperties}
          />
          <Tool
            variant="scheduler"
            text="Schedule 15 minutes for a quick meeting with Mike"
            className={clsx(styles.visual, styles.visual8)}
            style={{ '--index': 6 } as CSSProperties}
          />
        </Column>
      </Stack>
      {hasFireflies && <Fireflies targetRef={heroRef} className={styles.fireflies} />}
    </Section>
  )
}
